import { useEffect, useRef, useState, type RefObject } from 'react';
import { posApi } from '../api/pos';
import { billDateTime } from '../format';

/**
 * The customer's own details, asked for at the counter.
 *
 * The shop collects a name and a mobile number so it can send offers and
 * updates later, which is only worth anything if the customer agreed to it —
 * so the fields are required by default and staff have to tick a box to
 * say the customer would rather not share them. Nothing is stored when that
 * box is ticked.
 *
 * The number comes first: a customer who has been before gave their name on
 * an earlier bill, so once the ten digits are in the till fills the name in
 * from it and staff only have to check it with them.
 */
export interface CustomerDetailsValue {
  name: string;
  /** 10 digits, no country code — the +91 is fixed and added on submit. */
  phone: string;
  /** The customer declined. Makes both fields optional and sends neither. */
  declined: boolean;
}

export const EMPTY_CUSTOMER: CustomerDetailsValue = {
  name: '',
  phone: '',
  declined: false,
};

/** Indian mobile numbers are 10 digits and never start below 6. */
const MOBILE = /^[6-9]\d{9}$/;

/** Why this bill cannot be completed yet, or null when it can. */
export function customerProblem(value: CustomerDetailsValue): string | null {
  if (value.declined) return null;
  if (!MOBILE.test(value.phone)) {
    return 'Enter a 10-digit mobile number, or tick “prefers not to share”.';
  }
  if (!value.name.trim()) {
    return 'Enter the customer’s name, or tick “prefers not to share”.';
  }
  return null;
}

/** The half of the sale payload these fields own. Empty when declined. */
export function customerPayload(value: CustomerDetailsValue): {
  customerName?: string;
  customerPhone?: string;
} {
  if (value.declined || customerProblem(value)) return {};
  return {
    customerName: value.name.trim(),
    customerPhone: `+91${value.phone}`,
  };
}

/**
 * The mobile number in E.164, once one has been typed in full and the
 * customer has not declined; undefined until then. A coupon is checked
 * against it, so it is wanted before the name is.
 */
export function customerPhoneOf(value: CustomerDetailsValue): string | undefined {
  if (value.declined || !MOBILE.test(value.phone)) return undefined;
  return `+91${value.phone}`;
}

/**
 * What the collapsed header shows, so nothing has to be opened to be checked.
 * The confirmation dialog reads these details back with it too.
 */
export function customerSummary(value: CustomerDetailsValue): string {
  if (value.declined) return 'Not shared — customer declined';
  const parts = [value.name.trim(), value.phone && `+91 ${value.phone}`].filter(
    Boolean,
  );
  return parts.length ? parts.join(' · ') : 'Required before completing the sale';
}

export default function CustomerDetails({
  value,
  onChange,
  open,
  onToggle,
  problem,
  phoneRef,
  nameRef,
}: {
  value: CustomerDetailsValue;
  onChange: (next: CustomerDetailsValue) => void;
  open: boolean;
  onToggle: () => void;
  /** Set once staff have tried to complete the sale without them. */
  problem: string | null;
  phoneRef: RefObject<HTMLInputElement>;
  nameRef: RefObject<HTMLInputElement>;
}) {
  const set = (patch: Partial<CustomerDetailsValue>) =>
    onChange({ ...value, ...patch });

  // What the last bill on this number said, once it has been asked.
  const [found, setFound] = useState<{
    phone: string;
    name: string;
    lastVisit: string | null;
  } | null>(null);
  // The answer arrives after the render that asked, so it has to be applied
  // to the details as they are then, not as they were when it was sent.
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };

  const lookupPhone = customerPhoneOf(value);
  useEffect(() => {
    if (!lookupPhone) return;
    const controller = new AbortController();
    posApi
      .counterCustomer(lookupPhone, controller.signal)
      .then(({ name, lastVisit }) => {
        if (!name) return;
        setFound({ phone: lookupPhone, name, lastVisit });
        const { value: now, onChange: change } = latest.current;
        // Never over a name staff have already typed: the customer may be
        // standing there telling them a different one.
        if (customerPhoneOf(now) === lookupPhone && !now.name.trim()) {
          change({ ...now, name });
        }
      })
      // A failed lookup only means the name is typed by hand, as it always was.
      .catch(() => {});
    return () => controller.abort();
  }, [lookupPhone]);

  const known = found && lookupPhone === found.phone ? found : null;
  // Still the name the last bill gave — tinted until staff change it.
  const autofilled = !!known && !value.declined && value.name === known.name;

  function setPhone(raw: string) {
    const phone = raw.replace(/\D/g, '').slice(0, 10);
    // A filled-in name belongs to the number it came from. Correcting a
    // mistyped digit should not leave the wrong customer's name on the bill.
    const filled = found && value.name === found.name && phone !== found.phone;
    set({ phone, ...(filled ? { name: '' } : {}) });
  }

  return (
    <section className={problem ? 'customer customer-bad' : 'customer'}>
      <button
        type="button"
        className="customer-head"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="customer-body"
      >
        <span className="customer-head-main">
          <span className="field-label">Customer details</span>
          <span className="customer-summary">{customerSummary(value)}</span>
        </span>
        <span className="customer-chevron" aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div className="customer-body" id="customer-body">
          <label className="customer-field">
            <span className="field-label">Mobile number</span>
            <span className="phone-input">
              <span className="phone-prefix">+91</span>
              <input
                ref={phoneRef}
                className="input"
                value={value.phone}
                // Digits only, and never more than ten: staff are typing
                // while the customer reads it out, and a stray character here
                // becomes a number the offers never reach.
                onChange={(e) => setPhone(e.target.value)}
                disabled={value.declined}
                inputMode="numeric"
                placeholder="98765 43210"
                autoComplete="off"
              />
            </span>
            {known && !value.declined && (
              <span className="muted small">
                Returning customer
                {known.lastVisit ? ` — last bill ${billDateTime(known.lastVisit)}` : ''}
                {value.name.trim() && value.name !== known.name
                  ? `, under “${known.name}”`
                  : '. Check the name with them.'}
              </span>
            )}
          </label>
          <label className="customer-field">
            <span className="field-label">Name</span>
            <input
              ref={nameRef}
              className={autofilled ? 'input input-autofilled' : 'input'}
              value={value.name}
              // Selected on the way in, so a name the customer corrects is
              // typed straight over rather than deleted first.
              onFocus={(e) => {
                if (autofilled) e.currentTarget.select();
              }}
              onChange={(e) => set({ name: e.target.value })}
              disabled={value.declined}
              placeholder="Customer’s name"
              autoComplete="off"
              maxLength={100}
            />
          </label>


          <label className="customer-optout">
            <input
              type="checkbox"
              checked={value.declined}
              onChange={(e) => set({ declined: e.target.checked })}
            />
            <span>Customer prefers not to share their details</span>
          </label>

          <p className="muted small">
            Collected with the customer’s consent, to send them offers, promotions
            and updates. Required unless the box above is ticked.
          </p>

          {problem && (
            <p className="error small" role="alert">
              {problem}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
